"use client";
import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import TimelineSection from "../OurServices/TimelineSection";
import HowItWorks from "../HomePage/HowItWorks";
import { useTheme } from "../../Components/ThemeContext";
import SEOHead from "../../Components/SEOHead";

// ─── JSON-LD Schema ───────────────────────────────────────────────────────────
const servicesSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Software & Engineering Services",
  "provider": {
    "@type": "Organization",
    "name": "Fiieve",
    "url": "https://fiieve.com"
  },
  "description": "Fiieve delivers web development, mobile apps, AI automation, UI/UX design, IoT, embedded systems, robotics, PCB design, and smart home solutions for startups and enterprises.",
  "areaServed": "GB",
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",     "item": "https://fiieve.com/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://fiieve.com/services" }
    ]
  }
});

const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

// ─── Animated counter ────────────────────────────────────────────────────────
function CountUp({ target, duration = 1200 }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!inView) return;
    if (target.includes("–") || target.includes("-")) {
      const timeout = setTimeout(() => setDisplay(target), 400);
      return () => clearTimeout(timeout);
    }
    const numeric = parseInt(target.replace(/\D/g, ""));
    const suffix  = target.replace(/[\d]/g, "");
    let start = 0;
    const increment = numeric / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= numeric) {
        setDisplay(`${numeric}${suffix}`);
        clearInterval(timer);
      } else {
        setDisplay(`${Math.floor(start)}${suffix}`);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return <span ref={ref}>{display}</span>;
}

export default function Services() {
  const { isDark } = useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const navigate = useNavigate();

  // ✅ SEO: stat labels are trust signals per SEO doc
  const serviceCards = [
    { number: "98%", label: "Client Satisfaction Rate",  icon: "/Images/satisfaction.png" },
    { number: "40%", label: "Average ROI Increase",      icon: "/Images/roi.png" },
    { number: "3–6", label: "Weeks Average Delivery",    icon: "/Images/time.png" },
    { number: "50+", label: "Projects Delivered",        icon: "/Images/award.png" },
  ];

  // ✅ Paths updated to match App.js routes
  const softwareServices = [
    {
      id: 1,
      title: "Mobile App Development",
      heading: "MOBILE APP DEVELOPMENT",
      img: "/Images/mobileapp.png",
      description: "Cross-platform and native mobile apps for iOS and Android — built for seamless UX, speed, and scalability.",
      path: "/mobile-app-development",
    },
    {
      id: 2,
      title: "Web Development",
      heading: "WEB DEVELOPMENT",
      img: "/Images/webdev.png",
      description: "Fast, secure, and scalable web applications built with React, Next.js, and Node.js — optimised for performance and SEO.",
      path: "/web-development",
    },
    {
      id: 3,
      title: "Graphic & UI/UX Design",
      heading: "GRAPHIC & UI/UX DESIGN",
      img: "/Images/uiux.png",
      description: "User-centred interfaces that convert visitors into customers — from wireframes and prototypes to full design systems.",
      path: "/ui-ux-design",
    },
    {
      id: 4,
      title: "Custom Software Development",
      heading: "CUSTOM SOFTWARE DEVELOPMENT",
      img: "/Images/consultancy.png",
      description: "Bespoke software solutions built from scratch — tailored to your exact business requirements, scalable from day one.",
      path: "/custom-software-development",
    },
    {
      id: 5,
      title: "AI Automation",
      heading: "AI AUTOMATION",
      img: "/Images/ai.png",
      description: "Custom AI automation workflows and intelligent agents that eliminate repetitive tasks and optimise business operations 24/7.",
      path: "/ai-automation",
    },
  ];

  // ✅ Engineering services added — paths use engineering sub-routes
  const engineeringServices = [
    {
      id: 6,
      title: "IoT Development",
      heading: "IOT DEVELOPMENT",
      img: "/Images/iot.png",
      description: "End-to-end IoT systems — from sensor nodes and firmware to cloud dashboards and mobile control interfaces.",
      path: "/engineering/iot",
    },
    {
      id: 7,
      title: "Embedded Systems",
      heading: "EMBEDDED SYSTEMS",
      img: "/Images/embedded.png",
      description: "Real-time firmware for ARM Cortex, STM32, AVR, and RISC-V platforms using bare-metal C/C++ and FreeRTOS.",
      path: "/engineering/embedded-systems",
    },
    {
      id: 8,
      title: "Robotics & Automation",
      heading: "ROBOTICS & AUTOMATION",
      img: "/Images/robotics.png",
      description: "Autonomous and semi-autonomous robotic systems for research, industrial, and educational applications using ROS2.",
      path: "/engineering/robotics",
    },
    {
      id: 9,
      title: "PCB Design",
      heading: "PCB DESIGN",
      img: "/Images/pcb.png",
      description: "Professional PCB design from schematic to Gerber output — single-layer to HDI boards optimised for manufacturability.",
      path: "/engineering/pcb-design",
    },
    {
      id: 10,
      title: "Smart Home Systems",
      heading: "SMART HOME SYSTEMS",
      img: "/Images/smarthome.png",
      description: "Custom smart home and building automation — lighting, climate, security, and whole-home hubs with Home Assistant and Matter.",
      path: "/engineering/smart-home",
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show:   { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
  };

  const greenHeading = isDark ? "text-green-400" : "text-green-700";
  const headingText  = isDark ? "text-white"     : "text-gray-900";
  const bodyText     = isDark ? "text-gray-300"  : "text-gray-600";
  const statCardBg   = isDark ? "bg-gray-900/80" : "bg-[#1a2e1a]";
  const svcCardFrom  = isDark ? "from-[#0d0d0d]" : "from-[#1a2e1a]";
  const svcCardTo    = isDark ? "to-[#1a1a1a]"   : "to-[#0f1f0f]";

  // Reusable service card grid
  const ServiceGrid = ({ services }) => (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true, amount: 0.1 }}
      className="max-w-full mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6"
    >
      {services.map((service, index) => (
        <motion.article
          key={service.id}
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut", delay: index * 0.1 }}
          viewport={{ once: true, amount: 0.2 }}
          onClick={() => navigate(service.path)}
          role="link"
          aria-label={`Learn more about ${service.title}`}
          className={`bg-gradient-to-b ${svcCardFrom} ${svcCardTo} border border-green-400/40 rounded-2xl shadow-[0_0_25px_rgba(16,185,129,0.15)] p-5 flex flex-col items-center text-center hover:-translate-y-6 hover:shadow-[0_0_35px_rgba(16,185,129,0.4),0_0_25px_rgba(255,165,0,0.2)] hover:scale-105 transition-all duration-300 min-h-[320px] cursor-pointer`}
        >
          <img
            src={service.img}
            alt={`${service.title} service — Fiieve`}
            className="w-20 h-20 sm:w-24 sm:h-24 object-contain mb-4 flex-shrink-0"
            loading="lazy"
            width={96}
            height={96}
          />
          {/* ✅ SEO: h3 under section h2 — correct heading hierarchy */}
          <h3 className="text-base sm:text-lg font-semibold text-green-400 mb-2 leading-tight flex-shrink-0">
            {service.heading}
          </h3>
          <p className="text-gray-300 text-xs sm:text-sm mb-5 leading-relaxed flex-1 line-clamp-4">
            {service.description}
          </p>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(service.path); }}
            aria-label={`Learn more about ${service.title}`}
            className="border-2 border-green-400 text-green-400 px-5 py-2 rounded-full font-medium text-sm transition-all duration-300 hover:bg-green-400 hover:text-black flex-shrink-0"
          >
            Learn More
          </button>
        </motion.article>
      ))}
    </motion.div>
  );

  return (
    <>
      {/* ───── SEO Head ───── */}
      <SEOHead
        title="Software Development Services | Fiieve"
        description="Fiieve offers web development, mobile apps, AI automation, UI/UX design, IoT, embedded systems, robotics, and PCB design services for startups and enterprises across the UK."
        canonical="https://fiieve.com/services"
        ogImage="https://fiieve.com/Images/og-services.jpg"
        schema={servicesSchema}
      />

      <main
        className={`relative overflow-hidden pt-20 sm:pt-24 lg:pt-28 transition-colors duration-300 ${
          isDark ? "bg-black text-white" : "bg-[#f0f4f0] text-gray-900"
        }`}
        style={isDark ? darkGridStyle : lightGridStyle}
      >
        {!isDark && (
          <div className="pointer-events-none fixed inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <div style={{ position: "absolute", top: "-10%", left: "-5%", width: "55vw", height: "55vw", background: "radial-gradient(circle, rgba(74,222,128,0.13) 0%, transparent 70%)", filter: "blur(40px)" }} />
            <div style={{ position: "absolute", top: "-8%", right: "-8%", width: "50vw", height: "50vw", background: "radial-gradient(circle, rgba(251,146,60,0.11) 0%, transparent 70%)", filter: "blur(40px)" }} />
            <div style={{ position: "absolute", top: "40%", left: "20%", width: "60vw", height: "40vw", background: "radial-gradient(ellipse, rgba(74,222,128,0.07) 0%, transparent 65%)", filter: "blur(55px)" }} />
          </div>
        )}

        <div className="relative" style={{ zIndex: 1 }}>

          {/* ===== HERO BANNER ===== */}
          <div className="relative h-[25vh] overflow-hidden" style={{ marginBottom: "-3px" }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/Images/bg.png')" }} aria-hidden="true" />
            <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-8 h-full flex flex-col items-center justify-center text-center">
              {/* ✅ SEO: H1 */}
              <h1 className="text-3xl md:text-5xl font-extrabold text-green-400">
                Services
              </h1>
              {/* ✅ SEO: Visible breadcrumb matching JSON-LD */}
              <nav aria-label="Breadcrumb" className="mt-3">
                <ol className="flex items-center justify-center gap-1 text-sm text-white" role="list">
                  <li><a href="/" className="hover:text-green-400 transition-colors">Home</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li aria-current="page" className="text-green-400">Services</li>
                </ol>
              </nav>
            </div>
          </div>

          {/* ===== INTRO — SRS 3.1 subheadline ===== */}
          <motion.div
            className="text-center pt-8 pb-2 px-6"
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          >
            <p className={`text-sm font-semibold uppercase tracking-widest ${greenHeading} mb-1`}>
              What We Offer
            </p>
            {/* ✅ SEO: H2 — 2 liner per SRS 3.1 */}
            <h2 className={`text-2xl md:text-3xl font-bold ${headingText} mb-3 max-w-2xl mx-auto`}>
              From web apps to mobile platforms —{" "}
              <span className={greenHeading}>software engineered for performance, reliability, and growth.</span>
            </h2>
            <p className={`${bodyText} text-base max-w-xl mx-auto leading-relaxed`}>
              One expert team delivering end-to-end software and engineering solutions for startups and enterprises.
            </p>
          </motion.div>

          {/* ===== STATS ===== */}
          <motion.div
            className="max-w-7xl mx-auto px-6 py-12"
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
              {serviceCards.map((card, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.08, boxShadow: "0 0 30px rgba(34,197,94,0.6)" }}
                  transition={{ type: "spring", stiffness: 200, damping: 12 }}
                  className={`border border-green-500/50 rounded-2xl p-8 text-center hover:border-green-500 transition-all duration-300 ${statCardBg} flex flex-col items-center`}
                >
                  <img
                    src={card.icon}
                    alt={card.label}
                    className="w-20 h-20 mb-4 object-contain"
                    loading="lazy"
                    width={80}
                    height={80}
                  />
                  <div className="text-4xl font-extrabold text-green-500 mb-2">
                    <CountUp target={card.number} />
                  </div>
                  <p className="text-gray-300 text-lg font-semibold">{card.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ===== SOFTWARE SERVICES ===== */}
          <section
            aria-labelledby="software-services-heading"
            className={`w-full pb-10 px-4 sm:px-10 lg:px-20 ${isDark ? "bg-black" : "bg-transparent"}`}
          >
            <div className="text-center mb-8">
              <p className={`text-sm font-semibold uppercase tracking-widest ${greenHeading} mb-1`}>
                Digital Solutions
              </p>
              {/* ✅ SEO: H2 — software services section */}
              <h2 id="software-services-heading" className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${headingText} mb-2`}>
                Software Services
              </h2>
              <p className={`${bodyText} max-w-xl mx-auto text-sm md:text-base`}>
                Web development, mobile apps, AI automation, UI/UX design, and IT consultancy — all under one roof.
              </p>
            </div>
            <ServiceGrid services={softwareServices} />
          </section>

          {/* ===== ENGINEERING SERVICES ===== */}
          <section
            aria-labelledby="engineering-services-heading"
            className={`w-full pb-10 px-4 sm:px-10 lg:px-20 mt-4 ${isDark ? "bg-black" : "bg-transparent"}`}
          >
            <div className="text-center mb-8">
              <p className={`text-sm font-semibold uppercase tracking-widest ${greenHeading} mb-1`}>
                Hardware & Embedded
              </p>
              {/* ✅ SEO: H2 — engineering services section */}
              <h2 id="engineering-services-heading" className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${headingText} mb-2`}>
                Engineering Solutions
              </h2>
              <p className={`${bodyText} max-w-xl mx-auto text-sm md:text-base`}>
                IoT, embedded systems, robotics, PCB design, and smart home — hardware meets software.
              </p>
            </div>
            <ServiceGrid services={engineeringServices} />
          </section>

          {/* ===== TIMELINE & PROCESS ===== */}
          <div className="mt-5">
            <TimelineSection />
          </div>
          <HowItWorks />

        </div>
      </main>
    </>
  );
}